import React, { useCallback, useState } from 'react';
import { Snackbar, Alert, AlertTitle, Button, Stack, Slide, Box, IconButton, Tooltip } from '@mui/material';
import type { SlideProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import {
  useSuperAdminReminders,
  reminderTiming,
  REMINDER_KIND_TEXTS,
} from '../../contexts/SuperAdminRemindersContext';
import type { SuperAdminReminder, SuperAdminReminderKind } from '../../services/SuperAdminService';
import { buildReminderHref } from './superAdminReminderLink';

/**
 * SuperAdminRemindersNotifier — hatırlatmaların sağ alttaki uyarı bandı.
 *
 * Veriyi SuperAdminRemindersContext'ten alır (başlıktaki zil ile AYNI kaynak) ve
 * kaynak türü × durum (süresi dolmuş / yaklaşan) kırılımında ayrı bant gösterir.
 *
 * Kapatma damgası grup bazlı ve günlüktür: kalem yenilenmediği sürece ertesi gün
 * yeniden gelir. Damgaya gruptaki kayıt anahtarları da yazılır; gün içinde listeye
 * yeni bir kayıt girerse uyarı kapatılmış sayılmaz ve tekrar gösterilir.
 *
 * NOT: Kapatma yalnızca bu bandı susturur; zil listesinde kayıt durmaya devam
 * eder (zil canlı durumu gösterir, kapatılabilir bir kutu değildir).
 */

type GroupKey = `${SuperAdminReminderKind}:${'expired' | 'expiring'}`;

const todayStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SlideLeft = (props: SlideProps) => <Slide {...props} direction="left" />;

/** Damga değeri: gün + gruptaki kayıt anahtarları. */
const stampValue = (rows: SuperAdminReminder[]) =>
  `${todayStamp()}#${rows.map((r) => r.key).sort().join(',')}`;

const SuperAdminRemindersNotifier: React.FC = () => {
  const navigate = useNavigate();
  const { reminders } = useSuperAdminReminders();

  // Bu oturumda elle kapatılanlar — damga yazılana kadar anında gizlemek için.
  const [dismissed, setDismissed] = useState<Set<GroupKey>>(new Set());

  const isStampedToday = useCallback((key: GroupKey, rows: SuperAdminReminder[]) => {
    try {
      return localStorage.getItem(`superAdminReminderNotifiedAt:${key}`) === stampValue(rows);
    } catch {
      return false;
    }
  }, []);

  const dismiss = (key: GroupKey, rows: SuperAdminReminder[]) => {
    try {
      localStorage.setItem(`superAdminReminderNotifiedAt:${key}`, stampValue(rows));
    } catch { /* localStorage erişilemezse sessizce geç */ }
    setDismissed((prev) => new Set(prev).add(key));
  };

  // Süresi dolmuş gruplar önce gösterilsin diye tür değil durum baz alınarak sıralanır.
  // Sözleşme/abonelik + ürünler artık şirket başına tek "BILLING" kartında birleşiyor.
  // DEMO: demosu yaklaşan/dolmuş şirketler (lisansa geçiş için tahsilat oluşturulmalı).
  const kinds: SuperAdminReminderKind[] = ['FISCAL_DEVICE', 'BILLING', 'DEMO'];
  const allGroups: { key: GroupKey; expired: boolean; kind: SuperAdminReminderKind; rows: SuperAdminReminder[] }[] = [
    ...kinds.map((kind) => ({
      key: `${kind}:expired` as GroupKey,
      expired: true,
      kind,
      rows: reminders.filter((r) => r.kind === kind && r.daysRemaining < 0),
    })),
    ...kinds.map((kind) => ({
      key: `${kind}:expiring` as GroupKey,
      expired: false,
      kind,
      rows: reminders.filter((r) => r.kind === kind && r.daysRemaining >= 0),
    })),
  ];

  const groups = allGroups.filter(
    (g) => g.rows.length > 0 && !dismissed.has(g.key) && !isStampedToday(g.key, g.rows)
  );

  if (groups.length === 0) return null;

  return (
    <Snackbar
      open
      autoHideDuration={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={SlideLeft}
      sx={{ maxWidth: 480 }}
    >
      <Stack spacing={1} sx={{ maxHeight: '70vh', overflowY: 'auto', width: '100%' }}>
        {groups.map(({ key, expired, kind, rows }) => {
          const texts = REMINDER_KIND_TEXTS[kind];
          // Liste zaten en kritik önce sıralı geliyor.
          const worst = rows[0];
          const detail = [worst.title, worst.subtitle].filter(Boolean).join(' · ');

          return (
            <Alert
              key={key}
              severity={expired ? 'error' : 'warning'}
              variant="filled"
              // NOT: MUI'de `action` verildiğinde varsayılan kapatma ikonu render
              // edilmez; bu yüzden "Git" ile kapatma düğmesi birlikte veriliyor.
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      dismiss(key, rows);
                      navigate(buildReminderHref(worst));
                    }}
                    sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                  >
                    {texts.goLabel}
                  </Button>
                  <Tooltip title="Bu hatırlatmayı bugün için kapat">
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => dismiss(key, rows)}
                      aria-label="Kapat"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              sx={{ width: '100%' }}
            >
              <AlertTitle>
                {expired ? texts.expiredTitle(rows.length) : texts.expiringTitle(rows.length)}
              </AlertTitle>
              {detail} — {reminderTiming(worst.daysRemaining)}
              {rows.length > 1 && ` · ve ${rows.length - 1} kayıt daha`}
            </Alert>
          );
        })}
      </Stack>
    </Snackbar>
  );
};

export default SuperAdminRemindersNotifier;
